/**
 * Pure model for the Human Call tab's "All fields" board (right pane).
 *
 * {@link buildFieldBoard} enumerates EVERY output leaf field (top-level scalars +
 * entity-instance/nested) with its live {@link FieldStatus}, grouped by schema group.
 * The recursive flatten is delegated to {@link walkTemplateToLeaves} so the board and
 * the guided script share one keying/nesting rule; each leaf carries its entity
 * `instance` + `section` so the board can render nested sub-groups (right pane).
 *
 * This module is PURE (no React, no I/O) so it can be unit-tested in isolation.
 */
import type { FieldGroup, FormField } from '@/types/formSchema';

import type { FieldStatus } from './fieldStatus';
import { isEmptyValue } from './fieldStatus';
import type { LeafInstance, StatusCtx, TreeLeaf } from './fieldTree';
import { ENTITY_INPUT_KEY, walkTemplateToLeaves } from './fieldTree';

export interface BoardField {
  key: string;
  label: string;
  type: FormField['type'];
  status: FieldStatus;
  value: unknown;
  /** Schema field def — used by the board for inline editing. */
  field: FormField;
  /** Entity instance this leaf lives under (e.g. `drug[0]` + code label); undefined for top-level. */
  instance?: LeafInstance;
  /** Nearest enclosing nested-object label ("Coverage"); undefined for a direct leaf. */
  section?: string;
  /** Legacy compact sub-group label (section, else instance label). Prefer `instance`/`section`. */
  sub?: string;
  required: boolean;
}

export interface BoardSection {
  title: string;
  fields: BoardField[];
}

/** A run of fields sharing a section header within one instance (undefined = no header). */
interface BoardSubSection {
  section: string | undefined;
  fields: BoardField[];
}

/** Board fields grouped by entity instance (undefined = top-level), each split into sections. */
export interface BoardInstanceGroup {
  instance?: LeafInstance;
  sections: BoardSubSection[];
}

/**
 * Group already-filtered board rows into the right pane's nested render shape:
 * entity instance (undefined = top-level) → section sub-group → fields. Instance runs stay
 * consecutive (the walk emits an instance's fields contiguously), but sections are merged by
 * label WITHIN each instance — so a scalar before AND after a nested sub-object (both carrying
 * the parent's label) land in one section group, keeping section labels unique for safe React
 * keys (no duplicate-key mis-reconcile). PURE.
 */
export function groupBoardRows(fields: BoardField[]): BoardInstanceGroup[] {
  const groups: BoardInstanceGroup[] = [];
  for (const field of fields) {
    let group = groups[groups.length - 1];
    if (!group || group.instance?.key !== field.instance?.key) {
      group = { instance: field.instance, sections: [] };
      groups.push(group);
    }
    const existing = group.sections.find(s => s.section === field.section);
    if (existing) {
      existing.fields.push(field);
    } else {
      group.sections.push({ section: field.section, fields: [field] });
    }
  }
  return groups;
}

function leafToBoardField(leaf: TreeLeaf): BoardField {
  return {
    key: leaf.key,
    label: leaf.label,
    type: leaf.type,
    status: leaf.status,
    value: leaf.value,
    field: leaf.field,
    required: leaf.required,
    instance: leaf.instance,
    section: leaf.section,
    sub: leaf.section ?? leaf.instance?.label,
  };
}

/**
 * The effective value for a schema field before walking. Top-level ENTITY arrays
 * (`drug`/`procedure`/`sites_of_care`) are seeded from the input billable-code arrays so
 * their fields surface for capture even when nothing has been captured yet — mirroring the
 * script's `buildEntityUnits` fan-out (without seeding, an all-empty entity group produces
 * zero leaves and vanishes from the board — BI-1217). Non-array fields pass through.
 *
 * Per instance: keep the captured output element and its own `code`/`name`; only when those
 * are absent, borrow the positional input element's `code`/`name` (header + code leaf) — so
 * an output element's own code is never overwritten (no PHI mislabel under skew).
 */
/** Output element enriched with the input's `code`/`name` only where the output lacks them. */
function mergeInstance(out: unknown, inp: unknown): Record<string, unknown> {
  const o = (out ?? {}) as Record<string, unknown>;
  const i = (inp ?? {}) as Record<string, unknown>;
  const el: Record<string, unknown> = { ...o };
  if (isEmptyValue(el.code) && !isEmptyValue(i.code)) el.code = i.code;
  if (isEmptyValue(el.name) && !isEmptyValue(i.name)) el.name = i.name;
  return el;
}

function seedEntityValue(
  field: FormField,
  outputValue: unknown,
  inputData: Record<string, unknown>,
): unknown {
  // Only the known fan-out entities seed from an input code array. A generic array field
  // (not in ENTITY_INPUT_KEY) must NOT borrow from a same-named input array.
  const inputKey = ENTITY_INPUT_KEY[field.id];
  if (field.type !== 'array' || !inputKey) return outputValue;
  const outArr = Array.isArray(outputValue) ? outputValue : [];
  const inputArrRaw = inputData[inputKey];
  const inArr = Array.isArray(inputArrRaw) ? inputArrRaw : [];
  const count = Math.max(outArr.length, inArr.length);
  if (count === 0) return outArr;
  // Merge positionally at EVERY index (not only the input-longer tail) so an output element
  // lacking its own code borrows the positional input code — matching the script model's
  // `outElem.code ?? inElem.code`, so a drug labels identically on the board and the script.
  return Array.from({ length: count }, (_, i) => mergeInstance(outArr[i], inArr[i]));
}

/**
 * Enumerate EVERY output leaf field (top-level scalars + entity-instance/nested) with its
 * live status, grouped by schema group — the data for the right "All fields" board.
 * Entity arrays are seeded from `inputData` (see {@link seedEntityValue}) so their fields
 * appear before capture. PURE (no React/IO); unit-tested.
 */
export function buildFieldBoard(args: {
  outputSchemaGroups: FieldGroup[];
  outputData: Record<string, unknown>;
  /** Case input data — seeds entity instances (billable codes) so empty entities still show. */
  inputData?: Record<string, unknown>;
  missingFields: string[];
  requiredFields?: string[];
  heardKeys: Set<string>;
  sessionEditedKeys: Set<string>;
}): BoardSection[] {
  const ctx: StatusCtx = {
    missing: new Set(args.missingFields),
    required: new Set(args.requiredFields ?? args.missingFields),
    heard: args.heardKeys,
    edited: args.sessionEditedKeys,
  };
  const inputData = args.inputData ?? {};
  return args.outputSchemaGroups.map(group => ({
    title: group.label,
    fields: group.fields.flatMap(f =>
      walkTemplateToLeaves(f, f.id, seedEntityValue(f, args.outputData[f.id], inputData), ctx).map(
        leafToBoardField,
      ),
    ),
  }));
}
